/* eslint-disable no-shadow-restricted-names */

import _ from 'lodash';
import { PropsWithChildren, useEffect, useRef, useState } from 'react';
import { useIntersection } from 'react-use';
import { QueryResult } from '@apollo/client';

import * as graphql from '../../../../src/generated/graphql';

export function Infinity<T extends object>(
	props: PropsWithChildren<{
		query: QueryResult<T>;
		path: string;
		reverse?: boolean;
	}>,
) {
	const ref = useRef<HTMLDivElement | null>(null);
	const intersection = useIntersection(ref, {});
	const [retrigger, setRetrigger] = useState(false);

	const pageInfo = _.get(props.query.data, props.path).pageInfo as graphql.PageInfo;
	const loading = props.query.loading;

	useEffect(() => {
		if (!pageInfo || !intersection?.isIntersecting) {
			return;
		}

		if (!loading) {
			if (props.reverse) {
				if (!pageInfo.hasPreviousPage) {
					return;
				}
			} else {
				if (!pageInfo.hasNextPage) {
					return;
				}
			}

			(async () => {
				await props.query.fetchMore({
					variables: props.reverse
						? {
								last: props.query.variables!.last ?? 10,
								before: pageInfo.startCursor,
						  }
						: ({
								first: props.query.variables!.first ?? 10,
								after: pageInfo.endCursor,
								// eslint-disable-next-line @typescript-eslint/no-explicit-any
						  } as any),

					updateQuery: (prev, { fetchMoreResult }) => {
						if (!fetchMoreResult) {
							return prev;
						}

						const edges = props.path + '.edges';
						const pageInfo = props.path + '.pageInfo';

						const result = _.cloneDeep(prev);
						if (props.reverse) {
							_.set(result, edges, _.get(fetchMoreResult, edges).concat(_.get(prev, edges)));
						} else {
							_.set(result, edges, _.get(prev, edges).concat(_.get(fetchMoreResult, edges)));
						}
						_.set(result, pageInfo, _.get(fetchMoreResult, pageInfo));

						return result;
					},
				});
			})();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [loading, intersection?.isIntersecting]);

	if (props.reverse) {
		if (!pageInfo?.hasPreviousPage) {
			return null;
		}
	} else if (!pageInfo?.hasNextPage) {
		return null;
	}

	return (
		<div
			ref={(node) => {
				ref.current = node;

				if (!retrigger) {
					setRetrigger(true);
				}
			}}
			style={{ minHeight: 1 }}
		>
			{props.children}
		</div>
	);
}
